import AppForms from "@toothfairy/shared-ui/AppForms";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useCallback } from "react";
import { View } from "react-native";
import envConfig from "../../../../../envConfig";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../../Constants";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import Color from "../../../../Utility/Color";
import AdminInfoLeaf from "../../../Admin/Components/SubComponents/AdminInfoLeaf";
import { isLargeScreen } from "../../../MainViewContainer";

const CustomExecutionSettingsAIView = ({
  isEdit,
  settingType,
  handleDeletion,
  selectedId,
  availableAuthorisations,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const entityName = "Code exec.";

  const { state } = AppStateManager.useAppStateManager();
  const { getActiveWorkspace } = useWorkspaces();
  const _title = useCallback(() => {
    return isEdit ? `Save` : `Create`;
  }, [entityName, isEdit]);
  const { setFieldValue, values } = AppForms.useFormikContext();

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.border_color,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
      borderRadius: 10,
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      let color = new Color(data?.backgroundColor || currentTheme?.theme?.grey);
      return {
        ...styles,
        backgroundColor: isFocused
          ? currentTheme?.theme?.primary
          : currentTheme?.theme?.transparent,
        color: currentTheme?.theme?.white,
        cursor: "pointer",
      };
    },
    multiValue: (styles, { data }) => {
      let color = new Color(data?.backgroundColor || currentTheme?.theme?.grey);
      return {
        ...styles,
        backgroundColor: currentTheme?.theme?.primary,
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.white,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.primary,
      ":hover": {
        backgroundColor: currentTheme?.theme?.primary,
        color: "white",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.lightGray,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
  };

  return (
    <>
      <>
        <View>
          {isEdit && (
            <AdminInfoLeaf
              wrapperStyle={{
                alignSelf: "flex-start",
                marginBottom: 10,
              }}
              textContainer={{
                marginLeft: 5,
                marginTop: 10,
                marginBottom: 0,
              }}
              titleStyle={{
                marginTop: 0,
                marginLeft: 10,
              }}
              textStyle={{
                marginLeft: 0,
              }}
              title={"ID"}
              value={values?.id}
              width={420}
            />
          )}
          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            label={`Code execution env. name`}
            placeholder={`Name of the execution environment`}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="name"
          />
          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            multiline
            textContainer={{
              minHeight: 180,
              paddingVertical: 10,
              marginHorizontal: 5,
            }}
            label={`Description`}
            placeholder={`Description of what the code execution environment is used for`}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="description"
          />
          {getActiveWorkspace()?.subscriptionType == "enterprise" && (
            <AppForms.AppFormField
              showTextInputLabel
              wrapperInputStyle={{
                paddingTop: 10,
              }}
              label={`Custom remote environment name`}
              placeholder={`Name of the remote environment that will be used to execute the code. Please contact your TF account manager to setup one.`}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="functionName"
            />
          )}

          {values?.functionName && values?.functionName?.trim() != "" && (
            <AppForms.AppFormField
              showTextInputLabel
              wrapperInputStyle={{
                paddingTop: 10,
              }}
              multiline
              textContainer={{
                minHeight: 180,
                paddingVertical: 10,
                marginHorizontal: 5,
              }}
              label={`Available libraries`}
              placeholder={`List of the available libraries`}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="availableLibraries"
            />
          )}

          <AppForms.AppFormField
            showTextInputLabel
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            multiline
            textContainer={{
              minHeight: 180,
              paddingVertical: 10,
              marginHorizontal: 5,
            }}
            label={`Code execution instructions`}
            placeholder={`Additional set of instructions to be used to run and generate code executions`}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="customExecutionInstructions"
          />
          <AppForms.AppFormCodeEditor
            showTextInputLabel
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            multiline
            textContainer={{
              minHeight: 360,
              paddingVertical: 10,
            }}
            label={`Predefined code snippet`}
            placeholder={`Code that will be used to base the generation of the executable code by the agent`}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="customExecutionCode"
          />
          {typeof values?.customExecutionSecrets != "string" && (
            <AppForms.AppFormMultiSelect
              name="customExecutionSecrets"
              topErrorOffset={-21}
              touchedByDefault
              placeholder="Authorisations and secrets to inject in the execution environment"
              hidePlaceholder={true}
              items={availableAuthorisations
                ?.filter((u) => u?.type != "username_and_password")
                .map((u) => {
                  return {
                    ...u,
                    value: u?.id,
                    label: u?.name,
                    category: u?.type,
                  };
                })}
              uniqueKey="id"
              showDropDowns={true}
              readOnlyHeadings={true}
              labelOption="name"
              groupByProperty="type"
              style={{ maxWidth: 800 }}
              wrapperContainerStyle={{
                flex: !isLargeScreen() ? 1 : 0.5,
                zIndex: 20,
                paddingTop: 10,
                marginBottom: 40,
              }}
              styles={colourStyles}
              options={availableAuthorisations
                ?.filter((u) => u?.type != "username_and_password")
                ?.map((u) => {
                  return {
                    ...u,
                    value: u?.id,
                    label: u?.name,
                    category: u?.type,
                  };
                })}
            />
          )}

          <AppForms.AppFormSwitch
            // disabled={isEdit}
            // disabled={true}
            wrapperStyle={[
              {
                backgroundColor: isEdit
                  ? currentTheme?.theme?.grey
                  : currentTheme?.theme?.lightGrey,
                maxHeight: 30,
                paddingHorizontal: 10,
                paddingVertical: 18,
                margin: 5,
              },
            ]}
            labelTextStyle={{
              fontSize: currentTheme?.theme?.small_font_size,
              color: currentTheme?.theme?.darkGrey,
            }}
            showTextInputLabel={true}
            label={"Execute as static script"}
            displayIsTicked={true}
            name="hardcodedScript"
            value={values?.hardcodedScript}
            disabled={values?.isTemplate}
          />
          <AppForms.AppFormSwitch
            // disabled={isEdit}
            // disabled={true}
            wrapperStyle={[
              {
                backgroundColor: isEdit
                  ? currentTheme?.theme?.grey
                  : currentTheme?.theme?.lightGrey,
                maxHeight: 30,
                paddingHorizontal: 10,
                borderRadius: 20,
                paddingVertical: 18,
                margin: 5,
              },
            ]}
            labelTextStyle={{
              fontSize: currentTheme?.theme?.small_font_size,
              color: currentTheme?.theme?.darkGrey,
            }}
            showTextInputLabel={true}
            label={"Execute as python function"}
            displayIsTicked={true}
            name="isTemplate"
            value={values?.isTemplate}
            disabled={values?.hardcodedScript}
          />
        </View>
      </>
      <View style={{ alignItems: "center", zIndex: -1 }}>
        <AppForms.AppSubmitButton
          title={_title()}
          awesomeIconColor={currentTheme?.theme?.white}
          backgroundColor={currentTheme?.theme?.primary}
          btnContentStyle={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
          style={[
            {
              height: Constants.appButtonHeight,
              width: "auto",
              shadowColor: currentTheme?.theme?.transparent,
              marginHorizontal: 10,
            },
          ]}
          // btnLabelStyle={{ fontSize: 16 }}
          wrapperStyle={[
            {
              width: "fit-content",
              minWidth: 150,
              marginTop: 20,
              height: Constants.appButtonHeight,
            },
          ]}
        />
        {isEdit && (
          <ToothFairyButton
            title={"Delete"}
            onPress={handleDeletion}
            isDeletion
            wrapperStyle={{
              marginTop: 30,
              minWidth: 150,
              height: Constants.appButtonHeight,
            }}
          />
        )}
      </View>
    </>
  );
};

export default CustomExecutionSettingsAIView;
